import { ReactNode } from 'react';
import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { AUTH_TOKENS } from '../../pages/signup-login/auth/auth-branding';
import { BRAND_DISPLAY_FONT, GREEN_PALE_LIGHT } from './welcome-animations';

interface WelcomeStepCardProps {
  /** Material icon (rendered at 24px, inherits the tile's green ink). */
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  /** Extra styles — used to carry the per-card entrance `animation` delay. */
  sx?: SxProps<Theme>;
}

/**
 * A full-width clickable onboarding step (icon tile + title/subtitle + trailing
 * arrow). Rendered as a native `<button>` so it is keyboard-focusable and
 * Enter/Space-activatable, following the repo's Clickable Card Pattern
 * (`Paper variant="outlined"` + hover border/shadow).
 */
export const WelcomeStepCard = ({ icon, title, subtitle, onClick, sx }: WelcomeStepCardProps) => (
  <Paper
    component="button"
    type="button"
    variant="outlined"
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      textAlign: 'left',
      font: 'inherit',
      cursor: 'pointer',
      p: '16px 18px',
      borderRadius: '10px',
      borderColor: AUTH_TOKENS.border,
      bgcolor: '#fff',
      transition: 'border-color .15s ease, box-shadow .15s ease',
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: '0 4px 16px rgba(112,148,44,.16)',
      },
      ...sx,
    }}
  >
    <Box
      component="span"
      sx={{
        flex: 'none',
        width: 44,
        height: 44,
        borderRadius: '10px',
        bgcolor: GREEN_PALE_LIGHT,
        color: 'primary.dark',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {icon}
    </Box>
    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
      <Typography component="span" sx={{ display: 'block', fontSize: 15, fontFamily: BRAND_DISPLAY_FONT, color: 'text.primary', lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography component="span" sx={{ display: 'block', mt: '2px', fontSize: 13, color: AUTH_TOKENS.inkSecondary, lineHeight: 1.4 }}>
        {subtitle}
      </Typography>
    </Box>
    <ArrowForward sx={{ flex: 'none', fontSize: 22, color: AUTH_TOKENS.inkTertiary }} />
  </Paper>
);
